import { CTA } from '@volkswagen-onehub/components-core';
import {
  ArrowLeft as IconArrowLeft,
  ArrowRight as IconArrowRight,
} from '@volkswagen-onehub/icons-core';
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import type { ChildNodesIntersection } from './useChildNodesIntersection';
import useChildNodesIntersection from './useChildNodesIntersection';
import * as S from './Tabs.styled';

export enum TabsButtonsVariant {
  Default = 'default',
  Images = 'images',
  DetailedSpecs = 'detailedSpecs',
}

interface PolyfillTabsButtonsProps extends PropsWithChildren {
  variant?: TabsButtonsVariant;
  className?: string;
};

export const PolyfillTabsButtons: React.FC<PolyfillTabsButtonsProps> = ({
  children,
  variant = TabsButtonsVariant.Default,
  className,
}) => {
  const [parentNode, setParentNode] = useState<Element | null>(null);
  const [childNodes, setChildNodes] = useState<NodeListOf<Element> | null>(null);
  const containerReference = useRef<HTMLDivElement>(null);
  const { isFirstNodeIntersected, isLastNodeIntersected, records } = useChildNodesIntersection(
    parentNode,
    childNodes,
  );

  const findIntersectingTab = useCallback((tabs: ChildNodesIntersection[]) => {
    const intersectingTabIndex = tabs.findIndex((tab) => tab?.isIntersecting);

    return tabs[intersectingTabIndex - 1];
  }, []);

  const handlePrevious = useCallback(() => {
    if (!records.current) {
      return;
    }

    const tab = findIntersectingTab(Object.values(records.current));

    tab?.target.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }, [findIntersectingTab, records]);

  const handleNext = useCallback(() => {
    if (!records.current) {
      return;
    }

    const tab = findIntersectingTab(Object.values(records.current).reverse());

    tab?.target.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'end',
    });
  }, [findIntersectingTab, records]);

  useEffect(() => {
    if (containerReference.current) {
      setParentNode(containerReference.current.querySelector('[class^="StyledTabBar-"]'));
      setChildNodes(containerReference.current.querySelectorAll('[class^="StyledTab-"]'));
    }
  }, []);

  return (
    <S.PolyfillTabsButtonsContainer
      ref={containerReference}
      className={className}
      variant={variant}
    >
      {isFirstNodeIntersected && (
        <S.PolyfillTabsButtonsEdge direction={S.ArrowsDirection.Previous} variant={variant}>
          <CTA
            tag="button"
            size="small"
            emphasis="primary"
            ariaLabel={'aria here'}
            icon={<IconArrowLeft variant="small" />}
            onClick={handlePrevious}
          />
        </S.PolyfillTabsButtonsEdge>
      )}
      {children}
      {isLastNodeIntersected && (
        <S.PolyfillTabsButtonsEdge direction={S.ArrowsDirection.Next} variant={variant}>
          <CTA
            tag="button"
            size="small"
            emphasis="primary"
            ariaLabel={'aria here'}
            icon={<IconArrowRight variant="small" />}
            onClick={handleNext}
          />
        </S.PolyfillTabsButtonsEdge>
      )}
    </S.PolyfillTabsButtonsContainer>
  );
};
